import { createTransport, HttpAuthTransportSymbol } from '@goodt-common/net';
import { success, fail, Url } from '@goodt-common/utils';
import { SilentError } from '@goodt-wcore/errors';
import { ApiHttpClientError, createApiHttpClient } from '../ApiHttpClient';
import { ApiServiceError, ApiServiceErrorCode } from '../errors';
import { ApiHttpClientToApiServiceCodeMap } from './constants';

/**
 * @typedef {import('@goodt-common/net').TransportRequestCancel} TransportRequestCancel
 * @typedef {import('./types').ITransportResponse} ITransportResponse
 * @typedef {import('./types').ITransportOptions} ITransportOptions
 * @typedef {import('./types').ITransportExtraOptions} ITransportExtraOptions
 * @typedef {import('axios').CancelToken} CancelToken
 */

const DEFAULT_API_PATH_PREFIX = 'api';

/**
 *
 * @param {string} baseUrl
 * @param {string} pathPrefix
 * @return {string}
 */
const buildUrlWithoutPathPartsDuplicates = (baseUrl, pathPrefix) => {
    const re = new RegExp(`/${pathPrefix}/*$`);
    return re.test(baseUrl) ? baseUrl : Url.join(baseUrl, pathPrefix);
};

/**
 * @type {import('./').IApiService}
 */
export class BaseApiServiceBase {
    /**
     * @protected
     * @type {import('../ApiHttpClient').ApiHttpClient}
     */
    _client;

    /**
     * @protected
     * @type {import('./BaseApiService').IApiServiceRuntimeOptions}
     */
    _options = {};

    /**
     * @protected
     * @type {{ path: string }}
     */
    _apiBaseURLConfig = { path: DEFAULT_API_PATH_PREFIX };

    /**
     * @param {?import('../ApiHttpClient').ApiHttpClient} [client]
     * @param {?ITransport} [transport]
     * @param {?IApiServiceOptions} [options]
     * @throws {ApiServiceError}
     */
    constructor({ client, transport, options = {} } = {}) {
        // eslint-disable-next-line no-param-reassign, ban/ban
        transport ??= createTransport(HttpAuthTransportSymbol, undefined, {
            // disable http transport global error
            globalError: false
        });
        // eslint-disable-next-line no-param-reassign, ban/ban
        client ??= createApiHttpClient(transport);

        this._client = client;

        this.setOptions(options);
    }

    /**
     * @return {string}
     */
    get apiBaseURL() {
        return this._client?.baseURL ?? null;
    }

    /**
     *
     * @param {string} url
     */
    set apiBaseURL(url) {
        this._client.baseURL = this._buildBaseUrl(url);
    }

    /**
     *
     * @param {string} url
     * @return {string}
     * @private
     */
    _buildBaseUrl(url) {
        const { path } = this._apiBaseURLConfig;
        return buildUrlWithoutPathPartsDuplicates(url, path);
    }

    /**
     * @param {import('../types').IApiServiceOptions} options
     */
    setOptions(options) {
        const { apiBaseURL, apiBaseURLConfig, ...serviceOptions } = options;

        this._options = {
            ...this._options,
            ...serviceOptions
        };

        if (apiBaseURLConfig != null) {
            this._apiBaseURLConfig = apiBaseURLConfig;
        }

        if (apiBaseURL != null) {
            // eslint-disable-next-line no-param-reassign
            this.apiBaseURL = apiBaseURL;
        }
    }

    /**
     * @param {import('../types').IApiServiceRequest} request
     * @return {Promise<import('@goodt-common/utils').ISafeResult<*, Error>>}
     */
    async request(request) {
        try {
            const apiClientRequest = this._buildApiClientRequest(request);
            const result = await this._client.request(apiClientRequest);

            return success(result);
        } catch (error) {
            const processedError = this._processError(error);

            return fail(processedError);
        }
    }

    /**
     * Освобождает ресурсы, используемые сервисом
     */
    dispose() {
        this._client.dispose();
    }

    /**
     * return cancel function wich could be used to cancel next transport request
     * @return {TransportRequestCancel}
     */
    getNextRequestCancel() {
        return this._client.getNextRequestCancel();
    }

    /**
     * Билдит конфиг реквеста для клиента
     * @protected
     * @param {IApiServiceRequest} request
     * @return {import('../ApiHttpClient').IApiClientRequest} IApiClientRequest
     */
    // eslint-disable-next-line class-methods-use-this
    _buildApiClientRequest(request) {
        if (this._options.context != null) {
            request.options ??= {};
            request.options.context = {
                ...request.options.context,
                ...this._options.context
            };
        }

        return request;
    }

    /**
     * @protected
     * @param {Error} error
     * @return {ApiServiceError|Error|null}
     */
    _processError(error) {
        if (error instanceof SilentError) {
            return error;
        }

        return this._buildApiServiceError(error);
    }

    /**
     * @protected
     * @param {Error} error
     * @return {ApiServiceError}reason
     */
    // eslint-disable-next-line class-methods-use-this
    _buildApiServiceError(error) {
        const { message, code = ApiServiceError.Code.INTERNAL, reason = error } = error;
        let finalCode = code;

        if (error instanceof ApiHttpClientError) {
            finalCode = ApiHttpClientToApiServiceCodeMap[code] ?? ApiServiceErrorCode.INTERNAL;
        }

        return new ApiServiceError(message, {
            code: finalCode,
            reason
        });
    }
}

export { BaseApiServiceBase as default };
